import { useState, useRef, useEffect } from 'react';
import { TOGGLE_EXPERIMENT, MUTATION } from '../config.js';
import { getAppConfig } from '../utils/app-configs-utils.js';
import { transformCartResponse } from '../utils/cart-utils.js';
import { query } from '../utils/http-methods.js';
import { mutationHandlers } from '../utils/mutation-handlers.js';
import { getToggleAndExperimentValue } from '../utils/toggle-and-experiments.js';
import useArrayPush from './use-array-push.js';

const appConfig = getAppConfig();
const useCheckoutState = ({ onError }) => {
    const [cartVersion, setCartVersion] = useState('v1');
    const [checkoutState, setCheckoutState] = useState({
        autoshipment: {},
        cart: {},
        customer: {},
        delivery: {},
        stateAttributes: {},
        stateMessages: {},
        togglesAndExperiments: [],
        payment: {},
    });
    const [isFetching, setIsFetching] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [mainTogglesMap, setMainTogglesMap] = useState({});
    const [prevArticle, setPrevArticle] = useState({});
    const [pageType, setPageType] = useState('');
    const [isStickyAutoshipmentPresent, setIsStickyAutoshipmentPresent] = useState(false);
    const [isEditStepTriggered, setIsEditStepTriggered] = useState(false);
    const [hasPendingPageChanges, setHasPendingPageChanges] = useState(false);
    const [isLastContainerActive, setIsLastContainerActive] = useState(false);
    const [isDeliveryAndInvoiceCollapsed, setIsDeliveryAndInvoiceCollapsed] = useState(false);
    const [isPaymentCollapsed, setIsPaymentCollapsed] = useState(false);
    const [paymentFrontendErrors, addPaymentFrontendError] = useArrayPush();
    const [isDeliveryServicesProcessing, setIsDeliveryServicesProcessing] = useState(false);
    const [isSubscriptionValid, setIsSubscriptionValid] = useState(true);
    const isMounted = useRef(true);
    useEffect(() => {
        let isCancelled = false;
        const fetchData = async () => {
            setHasError(false);
            setIsFetching(true);
            try {
                const cartApiVersionResult = await query.getCartApiVersion();
                if (cartApiVersionResult.ok) {
                    const cartApiVersion = (await cartApiVersionResult.json()).version;
                    setCartVersion(cartApiVersion);
                    const result = await query.getState(cartApiVersion);
                    if (!isCancelled) {
                        if (result.ok) {
                            const resultData = transformCartResponse(await result.json());
                            setMainTogglesMap(getToggleAndExperimentValue(resultData?.togglesAndExperiments, TOGGLE_EXPERIMENT));
                            setCheckoutState(resultData);
                            setHasError(false);
                        }
                        else {
                            setHasError(true);
                            onError &&
                                onError(`Could not retrieve checkout state, status code: ${result.status}`);
                        }
                    }
                }
                else {
                    setHasError(true);
                    onError &&
                        onError(`Could not retrieve cart api version, status code: ${cartApiVersionResult.status}`);
                }
            }
            catch (err) {
                if (!isCancelled) {
                    setHasError(true);
                    onError && onError(err);
                }
            }
            if (!isCancelled) {
                setIsFetching(false);
            }
        };
        fetchData();
        return () => {
            isMounted.current = false;
            isCancelled = true;
        };
    }, []);
    const updateCheckoutState = async (mutation, mutationProps, csrfToken) => {
        let result;
        setHasError(false);
        setIsLoading(false);
        try {
            result = await mutationHandlers(mutation, setPrevArticle, setIsLoading, mutationProps, csrfToken, cartVersion);
            setCheckoutState(transformCartResponse(await result.json()));
            setHasError(false);
            setIsLoading(false);
        }
        catch (err) {
            setHasError(true);
            onError && onError(err);
            setIsLoading(false);
        }
    };
    const setCoupon = (body) => updateCheckoutState(MUTATION.SET_COUPON, body);
    const deleteCoupon = (body) => updateCheckoutState(MUTATION.DELETE_COUPON, body);
    const setArticleQuantity = (body) => updateCheckoutState(MUTATION.SET_ARTICLE_QUANTITY, body);
    const setSavingsPlanArticle = (body) => updateCheckoutState(MUTATION.SET_SAVING_PLAN_ARTICLE, body);
    const setFreeSampleArticle = (body) => updateCheckoutState(MUTATION.SET_FREE_SAMPLE_ARTICLE, body);
    const setShippingCountry = (body) => updateCheckoutState(MUTATION.SET_SHIPPING_COUNTRY, body);
    const setSubscriptionInterval = (body) => updateCheckoutState(MUTATION.SET_SUBSCRIPTION_INTERVAL, body);
    const removeSubscription = () => updateCheckoutState(MUTATION.REMOVE_SUBSCRIPTION);
    const createBasicGuestAccount = (body) => updateCheckoutState(MUTATION.CREATE_BASIC_GUEST_ACCOUNT, body, checkoutState.stateAttributes.csrfToken);
    const updateGuestAccountEmail = (body) => updateCheckoutState(MUTATION.UPADTE_GUEST_ACCOUNT_EMAIL, body);
    const setAddresses = (body) => updateCheckoutState(MUTATION.SET_ADDRESSES, body);
    const deleteSavedCard = (body) => updateCheckoutState(MUTATION.DELETE_SAVED_CARD, body);
    const deleteSavedBancontact = (body) => updateCheckoutState(MUTATION.DELETE_SAVED_BANCONTACT, body);
    const updatePreferredPayment = (body) => updateCheckoutState(MUTATION.UPDATE_PREFERRED_PAYMENT, body);
    const setDeliveryService = (body) => updateCheckoutState(MUTATION.SET_DELIVERY_SERVICE, body);
    const paypalOnApprove = (paypalOrderId) => console.log('This is paypal order ID: ' + paypalOrderId);
    const paypalOnCancel = () => console.log('Paypal transaction canceled');
    const applePayOnSubmit = (applePayToken) => console.log('This is apple pay token: ' + applePayToken);
    const applePayOnFatalError = (error) => console.log('This is apple pay error: ' + error);
    const braintreeOnSubmit = (deviceData, verifiedNonce) => console.log('This is the braintree device data ' +
        deviceData +
        'and this is the verified nonce ' +
        verifiedNonce);
    const braintreeOnError = (error) => console.log('This is braintree error: ' + error);
    return {
        checkoutState,
        cartVersion,
        appConfig,
        isFetching,
        isLoading,
        prevArticle,
        hasError,
        mainTogglesMap,
        pageType,
        isStickyAutoshipmentPresent,
        isEditStepTriggered,
        hasPendingPageChanges,
        isLastContainerActive,
        isDeliveryAndInvoiceCollapsed,
        isPaymentCollapsed,
        paymentFrontendErrors,
        isDeliveryServicesProcessing,
        isSubscriptionValid,
        onError,
        setPrevArticle,
        setPageType,
        setCoupon,
        deleteCoupon,
        setArticleQuantity,
        setSavingsPlanArticle,
        setFreeSampleArticle,
        setShippingCountry,
        setSubscriptionInterval,
        removeSubscription,
        setIsStickyAutoshipmentPresent,
        setIsEditStepTriggered,
        setHasPendingPageChanges,
        setIsLastContainerActive,
        setIsDeliveryAndInvoiceCollapsed,
        setIsPaymentCollapsed,
        addPaymentFrontendError,
        setIsDeliveryServicesProcessing,
        createBasicGuestAccount,
        updateGuestAccountEmail,
        setAddresses,
        deleteSavedCard,
        deleteSavedBancontact,
        updatePreferredPayment,
        setDeliveryService,
        paypalOnApprove,
        paypalOnCancel,
        applePayOnFatalError,
        applePayOnSubmit,
        braintreeOnSubmit,
        braintreeOnError,
        setIsSubscriptionValid,
    };
};

export { useCheckoutState as default };
//# sourceMappingURL=use-checkout-state.js.map
